/*
 * Copyright (c) 2025. Slimme Infrasoftware B.V. Apeldoorn /
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden
 * volgens de voorwaarden die zijn vastgelegd in de overeenkomst
 * tussen ATR en Slimme Infrasoftware B.V. en de onderliggende
 * overeenkomsten tussen Slimme Infrasoftware B.V en
 * Spin in het Web B.V.
 */

/*
* datatable2scherm.class.ts
* 12-9-2023 - Jelmer Jellema - Spin in het Web B.V.
*
* Abstracte parent van schermen met een standaard datatable2. We regelen zo de code zoveel mogelijk algemeen.
* Krijgt een injector van de concrete classes.
*
* Datatable2 is een niet compatibel modernisering van datatable. Wij bieden ze nog allebei
*
* Verwacht:
* - een datatable2 in de template met [data]="tableData" en [definition]="tableDefinition"
* - een concrete invulling van tableDefinition
* - een concrete invulling van tableQuery (zie onder) of een overrule van dataGetter (die een array met records teruggeeft)
* - editscherm: optioneel ingevuld.  Nodig als edit() en nieuw() gebruikt worden. Dit is een Editscherm-instance die daarvoor gebruikt wordt
*
* Algemene implementaties:
* edit(id): te gebruiken in de datatable tag: (rijklik)="edit($event.id)". Opent het editscherm met deze id.
* nieuw(): te gebruiken bij een nieuwknop, bijvoorbeeld in de footer van het scherm
* xls(): te geruiken bij een xls-knop, bijvoorbeeld in de footer van het scherm
*/

import {Appscherm} from './appscherm';
import {Directive, HostBinding, OnInit} from '@angular/core';
import {DatatableDef} from 'sihw-ng/datatable2/datatable2.component';
import {ApiService, Datarecords, Recordquery} from '../../services/api/api.service';
import {Basemodel} from '../../models/basemodel';

@Directive() //nodig want classes met ng-events of angular decorators moeten gedecoreerd zin
export abstract class Datatable2scherm extends Appscherm implements OnInit {
  //zet css class
  @HostBinding('class.datatablescherm') readonly _hostclass = true;

  public readonly abstract tableDefinition: DatatableDef; //public ivm AOT

  protected readonly model: typeof Basemodel = null; //optioneel: het model waarin de data wordt gegoten
  protected tableQuery: Recordquery = null; //niet abstract, want niet nodig als dataGetter wordt overschreven. Is de query zoals hij naar api.records gaat.
  public tableData: Datarecords | Basemodel[] = []; //de data voor in de datatable. Public ivm AOT
  protected readonly subscribeDatachanges: boolean = true; //zet op false en er wordt gewoon niet gereageerd op datachanges. Misschien is een reloader dan wel handig.

  protected readonly editscherm: string = null;

  private uitgesteldeUpdate: boolean = false; //gezet als er data wijzigt maar we niet actief zijn

  protected constructor(loglevel: string = 'warn') {
    super(loglevel);
  }

  ngOnInit(): void {
    //haal de data
    void this.haalData();

    //en init de subscription
    this.initDatawijzigsubscription();
  }

  /**
   * Open het editscherm voor de genoemde id
   * @param id
   */
  edit(id: number): void {
    if (this.editscherm) {
      void this.createWindow(this.editscherm, {id: id}, true); //dependent
    }
  }

  /**
   * Open het editscherm voor een nieuw record
   */
  nieuw(): void {
    if (this.editscherm) {
      void this.createWindow(this.editscherm, {}, true);
    }
  }

  /**
   * Haal de data opnieuw op. Wrapper om dataGetter, die overschreven kan worden
   */
  private async haalData(): Promise<void> {
    this.log.debug(`Async ophalen data`);
    this.tableData = await this.dataGetter();
    this.log.debug(`Data opgehaald`);
    await this.afterHaaldata();
  }

  /**
   * Voer haaldata uit als we actief zijn, anders inplannen
   * @protected
   */
  protected async planHaalData(): Promise<void> {
    if (this.actief) {
      await this.haalData();
    } else {
      this.uitgesteldeUpdate = true; //als we focus krijgen
    }
  }

  protected async afterHaaldata() {
    //overschrijfbaar
  }

  /**
   * Implementatie van het ophalen van de data. Kan overruled worden. Kan de data async teruggeven. Let op: overschrijf dan ook initDatawijzigsubscription
   * Let op: cast het zo nodig naar het juiste type
   */
  protected async dataGetter(): Promise<Datarecords | Basemodel[]> {
    if (this.tableQuery) {
      if (this.model) {
        return this.model.fromQuery(this.tableQuery);
      } else {
        return <Datarecords> await this.api.cachedRecords(this.tableQuery);
      }
    } else {
      throw(Error('Missende tableQuery'));
    }
  }

  /**
   * Maak een subscription op de datawijziging vanuit api
   * Overschrijf dit als niet de standaard datagetter wordt gebruikt
   */
  protected initDatawijzigsubscription(): void {
    if (this.tableQuery && this.subscribeDatachanges) {
      let tags = ApiService.tagsUitRecordquery(this.tableQuery);
      this.log.debug(`Datawijzigingsubscription op ${tags.join(', ')}`);
      this.subscription(this.api.onDatawijziging(tags).subscribe(() => {
        this.log.debug(`Inplannen ophalen data ivm datawijzigingssubscription`);
        this.planHaalData();
      })); //automtisch destroyen
    }
  }

  gettingFocus() {
    super.gettingFocus();
    if (this.uitgesteldeUpdate) {
      //dat moest nog
      this.uitgesteldeUpdate = false;
      void this.haalData();
    }
  }


}
